/**
 * configurator-app.js
 * Entry point for the ThreadTheme product configurator.
 * Manages state, step navigation, rendering, and Shopify cart integration.
 */

import { Viewer3D }        from './configurator-viewer.js';
import { ImageToneFilter } from './image-tone-filter.js';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const state = {
  config: null,
  steps: [],
  currentStep: 0,

  selected: {
    size: null,
    variant_id: null,        // size variant
    style: null,
    style_variant_id: null,
    frame_type: null,
    frame_type_variant_id: null,
    frame_color: null,
    frame_color_variant_id: null,
  },

  // Image pipeline
  originalUploadedDataUrl: null,
  croppedDataUrl: null,          // cropped image before tone filter
  uploadedDataUrl: null,
  cropMetadata: null,
  isAspectRatioSwapped: false,
  optimizationMode: 'off',

  // Tone filter
  toneFilterMode: 'original',    // 'original' | 'auto' | 'low-shadows' | 'low-lights'

  // Extras
  expressShipping: false,
  designerEditing: false,
  designerInstructions: '',

  currency: 'USD',
  language: 'en',
};

// ---------------------------------------------------------------------------
// DOM refs (populated after DOMContentLoaded)
// ---------------------------------------------------------------------------

const el = {};

function grabElements() {
  const ids = [
    'cfg-viewer-container',
    'cfg-step-title', 'cfg-step-indicator',
    'cfg-options-area',
    'cfg-prev-btn', 'cfg-next-btn',
    'cfg-prev-container', 'cfg-next-container',
    'cfg-add-to-cart', 'cfg-price-display',
    'cfg-upload-btn', 'cfg-file-input',
    'cfg-progress-bar',
  ];
  ids.forEach(id => { el[id] = document.getElementById(id); });
}

// ---------------------------------------------------------------------------
// Config: build from Liquid section blocks (window.cfgConfig) + viewer3d JSON
// ---------------------------------------------------------------------------

/**
 * Converts the flat Liquid-generated config (from section blocks) into the
 * nested object format the viewer and pricing engine expect.
 */
function buildConfigFromLiquid(liquidCfg, viewer3d) {
  const sizes   = liquidCfg.sizes        || [];
  const styles  = liquidCfg.styles       || [];
  const fcolors = liquidCfg.frame_colors || [];
  // size_type — unified rectangular + circular boards
  const size_type = {};
  sizes.forEach(s => {
    if (!s.key) return;
    const isCircle = s.shape === 'circular';
    const entry = {
      shape:          s.shape || 'rectangular',
      label:          s.label_inches || s.key,
      delivery_group: s.delivery_group || 'ready',
    };
    if (isCircle) {
      const diameter = s.circle_diameter || 300;
      const inset    = s.nail_area_inset || 10;
      entry.circle_diameter       = diameter;
      entry.circle_ring_thickness = s.circle_ring_thickness || 0;
      entry.nail_area_inset       = inset;
      entry.nail_area_diameter    = diameter - 2 * inset;
      entry.nail_plate_thickness  = s.nail_plate_thickness || 18;
      entry.art_size_multiplier   = s.art_size_multiplier > 0 ? +s.art_size_multiplier : 3;
      // art_line_width: 0 means "use global", otherwise convert from ×10 integer
      entry.art_line_width        = s.art_line_width_x10 > 0 ? s.art_line_width_x10 / 10 : null;
      // bw per-size overrides: 0 means "use global section setting"
      entry.bw_nail_count    = s.bw_nail_count_override    > 0 ? +s.bw_nail_count_override    : null;
      entry.bw_max_lines     = s.bw_max_lines_override     > 0 ? +s.bw_max_lines_override     : null;
      entry.bw_line_alpha    = s.bw_line_alpha_override    > 0 ? +s.bw_line_alpha_override    : null;
      entry.bw_min_angle_deg = s.bw_min_angle_deg_override > 0 ? +s.bw_min_angle_deg_override : null;
    } else {
      // Use > 0 check so Liquid default-of-0 never reaches processImage
      entry.plate_x     = s.plate_x     > 0 ? s.plate_x     : 572;
      entry.plate_y     = s.plate_y     > 0 ? s.plate_y     : 419;
      entry.nail_area_x = s.nail_area_x > 0 ? s.nail_area_x : 519;
      entry.nail_area_y = s.nail_area_y > 0 ? s.nail_area_y : 366;
    }
    size_type[s.key] = entry;
  });

  // style + background URL map (for _assetUrls.backgrounds)
  const style = {};
  const backgrounds = {};
  styles.forEach(s => {
    if (!s.key) return;
    style[s.key] = {
      label:                 s.label || s.key,
      group:                 s.group || 'white',
      background_type:       s.key,
      background_base_color: s.background_base_color || '#ffffff',
      yarn_color:            s.yarn_color            || '#000000',
      is_inverted:           !!s.is_inverted,
    };
    if (s.background_url) backgrounds[s.key] = s.background_url;
  });

  // frame_type — from blocks
  const frame_type = {};
  (liquidCfg.frame_types || []).forEach(ft => {
    if (!ft.key) return;
    frame_type[ft.key] = {
      label:                ft.label || ft.key,
      nail_plate_thickness: ft.nail_plate_thickness || 18,
      has_plexi:            false,
      has_colors:           !!ft.has_colors,
      board_style:          ft.board_style  || 'flat',
      bevel_radius:         ft.bevel_radius ?? 6,
    };
  });
  // Fallback: if no frame_type blocks configured, add 'none'
  if (Object.keys(frame_type).length === 0) {
    frame_type.none = { label: 'No frame', nail_plate_thickness: 18, has_plexi: false, has_colors: false };
  }

  // frame_color
  const frame_color = {};
  fcolors.forEach(fc => {
    if (!fc.key) return;
    frame_color[fc.key] = {
      hex:    fc.hex    || '#1c1c1c',
      finish: fc.finish || 'brushed',
      label:  fc.label  || fc.key,
    };
  });

  const s = liquidCfg.shader_settings || {};
  const baseShader = viewer3d.string_art_shader || { num_patches: 7, src_px_per_cm: 30, patch_size: 30, gamma: 1.0, min_clip: 0, max_clip: 245 };

  return {
    ...viewer3d,
    string_art_shader: {
      ...baseShader,
      ...(s.src_px_per_cm !== undefined && { src_px_per_cm: s.src_px_per_cm }),
      ...(s.patch_size    !== undefined && { patch_size:    s.patch_size }),
      ...(s.gamma         !== undefined && { gamma:         s.gamma / (s.gamma_scale || 10) }),
      ...(s.min_clip      !== undefined && { min_clip:      s.min_clip }),
      ...(s.max_clip      !== undefined && { max_clip:      s.max_clip }),
    },
    configurator_type:      liquidCfg.configurator_type      || 'nail',
    circular_art_type:     liquidCfg.circular_art_type     || 'colored',
    colored_art:           liquidCfg.colored_art           || {},
    bw_art:                liquidCfg.bw_art                || {},
    size_type,
    style,
    frame_type,
    frame_color,
    express_shipping:  { variant_id: liquidCfg.express_variant_id || 0, price_display: liquidCfg.express_price_display || 0 },
    designer_editing:  { price: { USD: 0, EUR: 0, GBP: 0 } },
    _backgrounds:      backgrounds,
  };
}

// ---------------------------------------------------------------------------
// Pricing
// ---------------------------------------------------------------------------

function computePrice() {
  let total = 0;

  const sizeCard = document.querySelector('[data-cfg-size-key][data-selected]');
  if (sizeCard) total += Number(sizeCard.dataset.price || 0);

  const styleCard = document.querySelector('[data-cfg-style-key][data-selected]');
  if (styleCard) total += Number(styleCard.dataset.price || 0);

  const ftCard = document.querySelector('[data-cfg-frame-type-key][data-selected]');
  if (ftCard) total += Number(ftCard.dataset.price || 0);

  const fcCard = document.querySelector('[data-cfg-frame-color-key][data-selected]');
  if (fcCard) total += Number(fcCard.dataset.price || 0);

  if (state.expressShipping) {
    total += state.config?.express_shipping?.price_display || 0;
  }

  return total;
}

function formatPrice(cents, currency = 'USD') {
  return new Intl.NumberFormat(navigator.language, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

// ---------------------------------------------------------------------------
// Step definitions
// ---------------------------------------------------------------------------

function buildSteps() {
  return [
    { key: 'size',        title: t('step_size_title') },
    { key: 'style',       title: t('step_style_title') },
    { key: 'frame_type',  title: t('step_frame_type_title') },
    { key: 'frame_color', title: t('step_frame_color_title') },
    { key: 'order',       title: t('step_order_title') },
  ];
}

// ---------------------------------------------------------------------------
// Simple i18n (reads from window.cfgI18n injected by Liquid)
// ---------------------------------------------------------------------------

function t(key) {
  return window.cfgI18n?.[key] || key;
}

// ---------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------

let viewer = null;

function navigate(delta) {
  const total = state.steps.length;
  const prev = state.currentStep;
  let next = Math.max(0, Math.min(total - 1, prev + delta));

  // Skip frame_color if selected frame_type has no colors
  if (state.steps[next]?.key === 'frame_color') {
    const selectedFtCard = document.querySelector('[data-cfg-frame-type-key][data-selected]');
    const hasColors = selectedFtCard?.dataset.hasColors === 'true';
    if (!hasColors) {
      next = Math.max(0, Math.min(total - 1, next + delta));
    }
  }

  if (next === prev) return;
  state.currentStep = next;
  history.pushState({ step: next }, '');
  renderCurrentStep(prev, next);
  scrollToStepTop();
}

/**
 * Smoothly scrolls so the top of the options body is visible just below
 * the sticky step header. On desktop, resets the internal cfg-body scroll.
 */
function scrollToStepTop() {
  if (window.innerWidth >= 1024) {
    const body = document.querySelector('.cfg-body');
    if (body) body.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }
  // Mobile: page scroll. Align cfg-body top with the sticky header's bottom edge.
  const header = document.querySelector('.cfg-header');
  const body   = document.querySelector('.cfg-body');
  if (!header || !body) return;
  const delta = body.getBoundingClientRect().top - header.getBoundingClientRect().bottom;
  if (Math.abs(delta) > 4) {
    window.scrollBy({ top: delta, behavior: 'smooth' });
  }
}

function renderCurrentStep(prevIdx, nextIdx) {
  if (prevIdx == null) prevIdx = nextIdx;
  const step = state.steps[state.currentStep];
  if (!step) return;

  // Rotate camera for new step
  if (viewer) {
    const prevKey = state.steps[prevIdx]?.key;
    viewer.rotateToStepAngle(step.key, prevKey);
  }

  updateStepHeader();
  updateProgressBar();
  updateNavButtons();
  renderStepContent(step.key);
  updatePriceDisplay();
}

function updateStepHeader() {
  const step = state.steps[state.currentStep];
  if (!step) return;
  const titleEl = document.getElementById('cfg-step-title');
  const indEl = document.getElementById('cfg-step-indicator');
  if (titleEl) titleEl.textContent = step.title;
  if (indEl) indEl.textContent = `${state.currentStep + 1} / ${state.steps.length}`;
}

function updateProgressBar() {
  const dots = document.querySelectorAll('[data-cfg-step-dot]');
  const lines = document.querySelectorAll('[data-cfg-step-line]');
  dots.forEach(dot => {
    const i = Number(dot.dataset.cfgStepDot);
    dot.classList.toggle('is-active', i === state.currentStep);
    dot.classList.toggle('is-done', i < state.currentStep);
    dot.classList.remove('is-active', 'is-done');
    if (i === state.currentStep) dot.classList.add('is-active');
    else if (i < state.currentStep) dot.classList.add('is-done');
  });
  lines.forEach(line => {
    const i = Number(line.dataset.cfgStepLine);
    line.classList.toggle('is-done', i < state.currentStep);
  });
}

function updateNavButtons() {
  const prevC = document.getElementById('cfg-prev-container');
  const nextC = document.getElementById('cfg-next-container');
  const atStart = state.currentStep === 0;
  const atEnd = state.currentStep >= state.steps.length - 1;

  if (prevC) {
    prevC.style.opacity = atStart ? '0' : '1';
    prevC.style.pointerEvents = atStart ? 'none' : 'auto';
  }
  if (nextC) {
    nextC.style.opacity = atEnd ? '0' : '1';
    nextC.style.pointerEvents = atEnd ? 'none' : 'auto';
  }
}

function updatePriceDisplay() {
  const el = document.getElementById('cfg-price-display');
  if (!el) return;
  const total = computePrice();
  el.textContent = total > 0 ? formatPrice(total, state.currency) : '—';
}

// ---------------------------------------------------------------------------
// Step content rendering (shows/hides pre-rendered panels)
// ---------------------------------------------------------------------------

function renderStepContent(stepKey) {
  document.querySelectorAll('[data-cfg-panel]').forEach(panel => {
    panel.hidden = panel.dataset.cfgPanel !== stepKey;
  });
  updateAddToCartButton();
  updateSummary();
}

// ---------------------------------------------------------------------------
// Selection helpers
// ---------------------------------------------------------------------------

function setSelection(key, value) {
  state.selected[key] = value;
  updatePriceDisplay();
  updateSummary();
  updateAddToCartButton();
}

function handleSizeSelect(card) {
  document.querySelectorAll('[data-cfg-size-key]').forEach(c => c.removeAttribute('data-selected'));
  card.setAttribute('data-selected', 'true');
  setSelection('size', card.dataset.cfgSizeKey);
  setSelection('variant_id', card.dataset.variantId);

  // Re-open crop modal so user can re-crop to the new aspect ratio
  if (state.originalUploadedDataUrl && !state._isDefaultPlaceholder) {
    openCropModal(state.originalUploadedDataUrl);
  } else if (viewer && state.uploadedDataUrl) {
    triggerModelUpdate();
  }
}

function handleStyleSelect(card) {
  document.querySelectorAll('[data-cfg-style-key]').forEach(c => c.removeAttribute('data-selected'));
  card.setAttribute('data-selected', 'true');
  setSelection('style', card.dataset.cfgStyleKey);
  setSelection('style_variant_id', card.dataset.variantId || null);
  if (viewer && state.uploadedDataUrl) triggerModelUpdate();
}

function handleFrameTypeSelect(card) {
  document.querySelectorAll('[data-cfg-frame-type-key]').forEach(c => c.removeAttribute('data-selected'));
  card.setAttribute('data-selected', 'true');
  setSelection('frame_type', card.dataset.cfgFrameTypeKey);
  setSelection('frame_type_variant_id', card.dataset.variantId || null);

  const hasColors = card.dataset.hasColors === 'true';
  document.querySelectorAll('[data-for-frame-type]').forEach(el => {
    el.hidden = el.dataset.forFrameType !== (hasColors ? 'standard' : 'none');
  });

  if (viewer && state.uploadedDataUrl) triggerModelUpdate();
}

function handleFrameColorSelect(card) {
  document.querySelectorAll('[data-cfg-frame-color-key]').forEach(c => c.removeAttribute('data-selected'));
  card.setAttribute('data-selected', 'true');
  setSelection('frame_color', card.dataset.cfgFrameColorKey);
  setSelection('frame_color_variant_id', card.dataset.variantId || null);
  if (viewer && state.uploadedDataUrl) triggerModelUpdate();
}

// ---------------------------------------------------------------------------
// Model update
// ---------------------------------------------------------------------------

let modelUpdateTimer = null;

function triggerModelUpdate() {
  clearTimeout(modelUpdateTimer);
  modelUpdateTimer = setTimeout(async () => {
    if (!viewer || !state.uploadedDataUrl || !state.selected.size) return;
    try {
      await viewer.updateModel(state);
    } catch (e) {
      console.error('[Configurator] Model update failed:', e);
    }
  }, 100);
}

// ---------------------------------------------------------------------------
// Crop modal
// ---------------------------------------------------------------------------

let _cropImg          = null;
let _cropCanvas       = null;
let _cropCtx          = null;
let _cropBox          = { x: 0, y: 0, w: 0, h: 0 };  // fixed centered crop rect in display px
let _cropAspect       = 1;
let _cropDrag         = null;                          // { startX, startY, origX, origY }
let _pendingCropUrl   = null;                          // raw data URL waiting to be cropped

// Image transform (centered on canvas)
let _imgOffset        = { x: 0, y: 0 };   // pan in canvas display px
let _imgZoom          = 1;                 // zoom multiplier
let _imgRotation90    = 0;                 // 90° rotation steps (degrees: 0/90/180/270)
let _imgStraighten    = 0;                 // fine rotation from slider (degrees)
let _baseScale        = 1;                 // scale to fit image into canvas at zoom=1
let _activePointers   = new Map();         // pointerId → {x,y} for pinch tracking
let _pinchStart       = null;             // { dist, zoom } at pinch start

function _cropRotRad() {
  return (_imgRotation90 + _imgStraighten) * Math.PI / 180;
}

function openCropModal(dataUrl) {
  _pendingCropUrl = dataUrl;  // held here until user confirms — NOT written to state yet

  const isCircular = state.config?.configurator_type === 'circular';
  const sizeConfig = state.config?.size_type?.[state.selected.size];
  const plateX = sizeConfig?.plate_x || 4;
  const plateY = sizeConfig?.plate_y || 3;

  const img = new Image();
  img.onload = () => {
    const imgW = img.naturalWidth;
    const imgH = img.naturalHeight;
    const imgAspect = imgW / imgH;

    let aspect;
    if (isCircular) {
      aspect = 1;
      state.isAspectRatioSwapped = false;
    } else {
      aspect = plateX / plateY;
      const swapped = (imgAspect < 1) !== (aspect < 1);
      state.isAspectRatioSwapped = swapped;
      if (swapped) aspect = 1 / aspect;
    }
    _cropAspect = aspect;
    _cropImg = img;

    _cropCanvas = document.getElementById('cfg-crop-canvas');
    if (!_cropCanvas) {
      state.originalUploadedDataUrl = dataUrl;
      state.croppedDataUrl          = dataUrl;
      state.uploadedDataUrl         = dataUrl;
      state._isDefaultPlaceholder   = false;
      updateAddToCartButton();
      _showFilterBar(true);
      applyToneFilter(state.toneFilterMode);
      return;
    }

    // Size canvas to fill container (max 560 × 300)
    const maxW = Math.min(560, window.innerWidth - 64);
    const maxH = 300;
    const dispW = maxW;
    const dispH = maxH;
    _cropCanvas.width  = dispW;
    _cropCanvas.height = dispH;
    _cropCtx = _cropCanvas.getContext('2d');

    // Scale to fit image into canvas (letterbox)
    _baseScale = Math.min(dispW / imgW, dispH / imgH);

    // Reset transform
    _imgOffset     = { x: 0, y: 0 };
    _imgRotation90 = 0;
    _imgStraighten = 0;

    // Crop box: centered at target aspect ratio, inset 10% from canvas edges for comfort
    const inset = 0.90;
    if (dispW / dispH > _cropAspect) {
      const cH = Math.round(dispH * inset);
      const cW = Math.round(cH * _cropAspect);
      _cropBox = { x: Math.round((dispW - cW) / 2), y: Math.round((dispH - cH) / 2), w: cW, h: cH };
    } else {
      const cW = Math.round(dispW * inset);
      const cH = Math.round(cW / _cropAspect);
      _cropBox = { x: Math.round((dispW - cW) / 2), y: Math.round((dispH - cH) / 2), w: cW, h: cH };
    }

    // Initial zoom: image covers the crop box
    const imgDispW = imgW * _baseScale;
    const imgDispH = imgH * _baseScale;
    _imgZoom = Math.max(1, _cropBox.w / imgDispW, _cropBox.h / imgDispH);

    // Reset UI controls
    const zoomEl = document.getElementById('cfg-crop-zoom');
    const strEl  = document.getElementById('cfg-crop-straighten');
    if (zoomEl) zoomEl.value = Math.round(_imgZoom * 100);
    if (strEl)  strEl.value  = 0;

    _drawCropCanvas();
    _initCropEvents();

    const modal = document.getElementById('cfg-crop-modal');
    if (modal) modal.style.display = 'flex';
  };
  img.src = dataUrl;
}

function _drawCropCanvas() {
  if (!_cropCtx || !_cropImg) return;
  const ctx = _cropCtx;
  const cw  = _cropCanvas.width;
  const ch  = _cropCanvas.height;
  const { x: bx, y: by, w: bw, h: bh } = _cropBox;
  const rot = _cropRotRad();
  const eff = _baseScale * _imgZoom;

  ctx.clearRect(0, 0, cw, ch);
  ctx.fillStyle = '#888';
  ctx.fillRect(0, 0, cw, ch);

  // Draw image with pan/zoom/rotation transform centered on canvas
  ctx.save();
  ctx.translate(cw / 2 + _imgOffset.x, ch / 2 + _imgOffset.y);
  ctx.rotate(rot);
  ctx.scale(eff, eff);
  ctx.drawImage(_cropImg, -_cropImg.naturalWidth / 2, -_cropImg.naturalHeight / 2);
  ctx.restore();

  // Dark overlay outside crop box
  ctx.fillStyle = 'rgba(0,0,0,0.52)';
  ctx.fillRect(0,       0,       cw,          by);
  ctx.fillRect(0,       by + bh, cw,          ch - by - bh);
  ctx.fillRect(0,       by,      bx,          bh);
  ctx.fillRect(bx + bw, by,      cw - bx - bw, bh);

  // Crop border
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.lineWidth   = 1.5;
  ctx.strokeRect(bx + 0.75, by + 0.75, bw - 1.5, bh - 1.5);

  // Rule-of-thirds
  ctx.strokeStyle = 'rgba(255,255,255,0.3)';
  ctx.lineWidth   = 0.5;
  for (let i = 1; i < 3; i++) {
    ctx.beginPath(); ctx.moveTo(bx + bw * i / 3, by);      ctx.lineTo(bx + bw * i / 3, by + bh); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(bx,               by + bh * i / 3); ctx.lineTo(bx + bw, by + bh * i / 3); ctx.stroke();
  }
}

function _setCropZoom(z) {
  _imgZoom = Math.max(0.3, Math.min(5, z));
  const el = document.getElementById('cfg-crop-zoom');
  if (el) el.value = Math.round(_imgZoom * 100);
  _drawCropCanvas();
}

function _initCropEvents() {
  if (!_cropCanvas) return;
  // Replace node to clear previous listeners
  const fresh = _cropCanvas.cloneNode(true);
  _cropCanvas.parentNode.replaceChild(fresh, _cropCanvas);
  _cropCanvas = fresh;
  _cropCtx    = _cropCanvas.getContext('2d');
  _activePointers.clear();
  _pinchStart = null;
  _cropDrag   = null;
  _drawCropCanvas();

  // ── Pointer: pan + pinch ──────────────────────────────
  _cropCanvas.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    const rect   = _cropCanvas.getBoundingClientRect();
    const scaleX = _cropCanvas.width  / rect.width;
    const scaleY = _cropCanvas.height / rect.height;
    const px = (e.clientX - rect.left) * scaleX;
    const py = (e.clientY - rect.top)  * scaleY;
    _activePointers.set(e.pointerId, { x: px, y: py });
    _cropCanvas.setPointerCapture(e.pointerId);

    if (_activePointers.size === 1) {
      _cropDrag = { startX: px, startY: py, origX: _imgOffset.x, origY: _imgOffset.y };
    } else if (_activePointers.size === 2) {
      _cropDrag = null;
      const pts = [..._activePointers.values()];
      _pinchStart = { dist: Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y), zoom: _imgZoom };
    }
  });

  _cropCanvas.addEventListener('pointermove', (e) => {
    if (!_activePointers.has(e.pointerId)) return;
    e.preventDefault();
    const rect   = _cropCanvas.getBoundingClientRect();
    const scaleX = _cropCanvas.width  / rect.width;
    const scaleY = _cropCanvas.height / rect.height;
    const px = (e.clientX - rect.left) * scaleX;
    const py = (e.clientY - rect.top)  * scaleY;
    _activePointers.set(e.pointerId, { x: px, y: py });

    if (_activePointers.size === 2 && _pinchStart) {
      const pts  = [..._activePointers.values()];
      const dist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
      _setCropZoom(_pinchStart.zoom * (dist / _pinchStart.dist));
    } else if (_activePointers.size === 1 && _cropDrag) {
      _imgOffset.x = _cropDrag.origX + (px - _cropDrag.startX);
      _imgOffset.y = _cropDrag.origY + (py - _cropDrag.startY);
      _drawCropCanvas();
    }
  });

  const _clearPointer = (e) => {
    _activePointers.delete(e.pointerId);
    if (_activePointers.size < 2) _pinchStart = null;
    if (_activePointers.size === 0) _cropDrag = null;
  };
  _cropCanvas.addEventListener('pointerup',     _clearPointer);
  _cropCanvas.addEventListener('pointercancel', _clearPointer);

  // ── Scroll wheel zoom ─────────────────────────────────
  _cropCanvas.addEventListener('wheel', (e) => {
    e.preventDefault();
    _setCropZoom(_imgZoom * (e.deltaY > 0 ? 0.92 : 1.08));
  }, { passive: false });

  // ── Rotate buttons ────────────────────────────────────
  document.getElementById('cfg-crop-rot-l')?.addEventListener('click', () => {
    _imgRotation90 = (_imgRotation90 - 90 + 360) % 360;
    _drawCropCanvas();
  });
  document.getElementById('cfg-crop-rot-r')?.addEventListener('click', () => {
    _imgRotation90 = (_imgRotation90 + 90) % 360;
    _drawCropCanvas();
  });

  // ── Straighten slider ─────────────────────────────────
  document.getElementById('cfg-crop-straighten')?.addEventListener('input', (e) => {
    _imgStraighten = parseFloat(e.target.value);
    _drawCropCanvas();
  });

  // ── Zoom slider ───────────────────────────────────────
  document.getElementById('cfg-crop-zoom')?.addEventListener('input', (e) => {
    _imgZoom = parseInt(e.target.value, 10) / 100;
    _drawCropCanvas();
  });
}

function _confirmCrop() {
  if (!_cropImg) return;

  const { x: bx, y: by, w: bw, h: bh } = _cropBox;
  const cw  = _cropCanvas.width;
  const ch  = _cropCanvas.height;
  const rot = _cropRotRad();
  const eff = _baseScale * _imgZoom;

  // Output at native image resolution for the cropped area
  const outW = Math.max(100, Math.round(bw / eff));
  const outH = Math.max(100, Math.round(bh / eff));

  const off  = document.createElement('canvas');
  off.width  = outW;
  off.height = outH;
  const octx = off.getContext('2d');

  // The image center in display coords: (cw/2 + offsetX, ch/2 + offsetY).
  // The crop box top-left in display coords: (bx, by).
  // Output pixel (ox, oy) corresponds to display pixel (bx + ox * bw/outW, by + oy * bh/outH).
  // Since outW = bw/eff, bw/outW = eff. So display pixel = (bx + ox*eff, by + oy*eff).
  //
  // Image center in output pixel coords:
  const outScale = outW / bw;  // ≈ 1/eff
  const icx = (cw / 2 + _imgOffset.x - bx) * outScale;
  const icy = (ch / 2 + _imgOffset.y - by) * outScale;
  const drawScale = eff * outScale;   // ≈ 1.0

  octx.save();
  octx.translate(icx, icy);
  octx.rotate(rot);
  octx.scale(drawScale, drawScale);
  octx.drawImage(_cropImg, -_cropImg.naturalWidth / 2, -_cropImg.naturalHeight / 2);
  octx.restore();

  if (_pendingCropUrl) state.originalUploadedDataUrl = _pendingCropUrl;

  const croppedUrl            = off.toDataURL('image/jpeg', 0.92);
  state.croppedDataUrl        = croppedUrl;
  state.uploadedDataUrl       = croppedUrl;
  state._isDefaultPlaceholder = false;

  _closeCropModal();
  updateAddToCartButton();
  _showFilterBar(true);
  applyToneFilter(state.toneFilterMode);
}

function _closeCropModal() {
  const modal = document.getElementById('cfg-crop-modal');
  if (modal) modal.style.display = 'none';
  _cropDrag = null;
  _activePointers.clear();
  _pinchStart = null;
}

// ---------------------------------------------------------------------------
// Image upload
// ---------------------------------------------------------------------------

function setupUpload(fileInput) {
  if (!fileInput) return;

  fileInput.addEventListener('change', (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      // Pass raw data URL to crop modal; state.originalUploadedDataUrl is only written
      // once the user confirms the crop (inside _confirmCrop).
      openCropModal(ev.target.result);
    };
    reader.readAsDataURL(file);

    // Reset so the same file can be re-uploaded
    fileInput.value = '';
  });
}

// ---------------------------------------------------------------------------
// Summary (Step 5)
// ---------------------------------------------------------------------------

function updateSummary() {
  const container = document.getElementById('cfg-summary-lines');
  if (!container) return;

  const cfg = state.config;
  const currency = state.currency;
  const sizeKey = state.selected.size;

  const rows = [];

  // Size
  const sizeLabel = sizeKey ? cfg?.size_type?.[sizeKey]?.label : '—';
  const sizeCard = document.querySelector('[data-cfg-size-key][data-selected]');
  const sizePrice = sizeCard ? Number(sizeCard.dataset.price || 0) : 0;
  rows.push({ label: t('summary_size'), value: sizeLabel || '—', price: sizePrice, step: 'size' });

  // Style
  const styleKey = state.selected.style;
  const styleLabel = styleKey ? cfg?.style?.[styleKey]?.label : '—';
  const styleCard = document.querySelector('[data-cfg-style-key][data-selected]');
  const stylePrice = styleCard ? Number(styleCard.dataset.price || 0) : 0;
  rows.push({ label: t('summary_style'), value: styleLabel || '—', price: stylePrice, step: 'style' });

  // Frame type
  const ftKey = state.selected.frame_type;
  const ftLabel = ftKey ? cfg?.frame_type?.[ftKey]?.label : '—';
  const ftCard = document.querySelector('[data-cfg-frame-type-key][data-selected]');
  const ftPrice = ftCard ? Number(ftCard.dataset.price || 0) : 0;
  rows.push({ label: t('summary_frame_type'), value: ftLabel || '—', price: ftPrice, step: 'frame_type' });

  // Frame color
  const fcKey = state.selected.frame_color;
  const fcLabel = fcKey ? cfg?.frame_color?.[fcKey]?.label : '—';
  const fcCard = document.querySelector('[data-cfg-frame-color-key][data-selected]');
  const fcPrice = fcCard ? Number(fcCard.dataset.price || 0) : 0;
  if (ftKey && ftKey !== 'none') {
    rows.push({ label: t('summary_frame_color'), value: fcLabel || '—', price: fcPrice, step: 'frame_color' });
  }

  // Express shipping
  if (state.expressShipping) {
    const expPrice = cfg?.express_shipping?.price_display ?? 0;
    rows.push({ label: t('extra_express'), value: '✓', price: expPrice, step: null });
  }

  // Total
  const total = computePrice();

  container.innerHTML = rows.map(row => `
    <div class="flex justify-between items-center px-4 py-2.5 text-sm ${row.step ? 'cursor-pointer hover:bg-zinc-50' : ''}"
         ${row.step ? `data-cfg-summary-nav="${row.step}"` : ''}>
      <span class="text-zinc-500">${row.label}</span>
      <span class="font-medium text-zinc-800 flex items-center gap-2">
        ${row.value}
        ${row.price > 0 ? `<span class="text-zinc-400 text-xs">${formatPrice(row.price, currency)}</span>` : ''}
      </span>
    </div>
  `).join('') + `
    <div class="flex justify-between px-4 py-3 bg-zinc-50 font-bold text-sm border-t border-zinc-100">
      <span>${t('summary_total')}</span>
      <span id="cfg-total-price">${total > 0 ? formatPrice(total, currency) : '—'}</span>
    </div>
  `;

  // Bind summary nav clicks
  container.querySelectorAll('[data-cfg-summary-nav]').forEach(row => {
    row.addEventListener('click', () => {
      const targetKey = row.dataset.cfgSummaryNav;
      const idx = state.steps.findIndex(s => s.key === targetKey);
      if (idx >= 0 && idx !== state.currentStep) {
        const prev = state.currentStep;
        state.currentStep = idx;
        history.pushState({ step: idx }, '');
        renderCurrentStep(prev, idx);
        scrollToStepTop();
      }
    });
  });
}

// ---------------------------------------------------------------------------
// Add to Cart
// ---------------------------------------------------------------------------

function updateAddToCartButton() {
  const btn = document.getElementById('cfg-add-to-cart');
  if (!btn) return;
  const canAdd = !!state.uploadedDataUrl && !state._isDefaultPlaceholder && !!state.selected.size && !!state.selected.variant_id;
  btn.disabled = !canAdd;
}

async function addToCart() {
  if (!state.selected.variant_id || !state.uploadedDataUrl) return;

  const btn = document.getElementById('cfg-add-to-cart');
  if (btn) { btn.disabled = true; btn.textContent = t('adding_to_cart'); }

  try {
    // ── 1. Build property values ────────────────────────────────────────────
    const styleLabel      = state.config?.style?.[state.selected.style]?.label       || state.selected.style || '';
    const frameLabel      = state.config?.frame_type?.[state.selected.frame_type]?.label  || state.selected.frame_type || '';
    const frameColorLabel = state.config?.frame_color?.[state.selected.frame_color]?.label || state.selected.frame_color || '';

    // ── 2. Addon items (no photo — JSON endpoint) ───────────────────────────
    // Bundle ID links the main item to its addons so they can be grouped/removed together
    const bundleId = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    const addonProps = { '_bundle_id': bundleId, '_bundle_role': 'addon' };

    const addonItems = [];
    if (state.selected.style_variant_id)       addonItems.push({ id: Number(state.selected.style_variant_id),       quantity: 1, properties: addonProps });
    if (state.selected.frame_type_variant_id)  addonItems.push({ id: Number(state.selected.frame_type_variant_id),  quantity: 1, properties: addonProps });
    if (state.selected.frame_color_variant_id) addonItems.push({ id: Number(state.selected.frame_color_variant_id), quantity: 1, properties: addonProps });
    if (state.expressShipping) {
      const expId = state.config?.express_shipping?.variant_id;
      if (expId) addonItems.push({ id: Number(expId), quantity: 1, properties: addonProps });
    }
    const validAddons = addonItems.filter(i => i.id > 0);

    if (validAddons.length) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: validAddons }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error('[Configurator] Addon cart error:', err);
        alert(t('cart_error'));
        return;
      }
    }

    // ── 3. Populate the hidden product form ─────────────────────────────────
    const form = document.getElementById('cfg-photo-form');
    if (!form) { window.location.href = '/cart'; return; }

    form.enctype = 'multipart/form-data';

    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput) variantInput.value = state.selected.variant_id;

    const _set = (id, val) => { const el = document.getElementById(id); if (el) el.value = val; };
    _set('cfg-prop-photo-editing',  state.designerEditing ? 'Yes' : '');
    _set('cfg-prop-style',          styleLabel);
    _set('cfg-prop-frame',          frameLabel);
    _set('cfg-prop-frame-color',    frameColorLabel);
    _set('cfg-prop-express',        state.expressShipping ? 'Yes' : '');
    _set('cfg-prop-instructions',   state.designerEditing && state.designerInstructions ? state.designerInstructions : '');
    _set('cfg-prop-bundle-id',      bundleId);
    _set('cfg-prop-bundle-role',    'main');

    // ── 4. Set customer photo on file input via DataTransfer ────────────────
    const photoSrc = state.originalUploadedDataUrl || state.uploadedDataUrl;
    const fileInput = document.getElementById('cfg-photo-file');
    if (fileInput && photoSrc) {
      const blob = await fetch(photoSrc).then(r => r.blob());
      const ext  = blob.type === 'image/png' ? 'png' : 'jpg';
      const file = new File([blob], `custom-photo.${ext}`, { type: blob.type || 'image/jpeg' });
      const dt   = new DataTransfer();
      dt.items.add(file);
      fileInput.files = dt.files;
    }

    // ── 5. Submit form → Shopify stores photo on CDN, redirects to /cart ───
    form.requestSubmit ? form.requestSubmit() : form.submit();

  } catch (e) {
    console.error('[Configurator] Cart request failed:', e);
    alert(t('cart_error'));
    if (btn) { btn.disabled = false; btn.textContent = t('add_to_cart'); }
  }
}

// ---------------------------------------------------------------------------
// Tone filter
// ---------------------------------------------------------------------------

/** Minimal luminance analysis (thumbnail 180px) for auto-recommendation. */
function _analyzeImageData(img) {
  const MAX = 180;
  const tmp = document.createElement('canvas');
  const scale = Math.min(MAX / img.naturalWidth, MAX / img.naturalHeight, 1);
  tmp.width  = Math.max(1, Math.round(img.naturalWidth  * scale));
  tmp.height = Math.max(1, Math.round(img.naturalHeight * scale));
  tmp.getContext('2d').drawImage(img, 0, 0, tmp.width, tmp.height);
  const data  = tmp.getContext('2d').getImageData(0, 0, tmp.width, tmp.height).data;
  const total = tmp.width * tmp.height;
  let sumLum = 0, sumSq = 0;
  const hist = new Array(256).fill(0);
  for (let i = 0; i < data.length; i += 4) {
    const lum = Math.round(0.2126 * data[i] + 0.7152 * data[i+1] + 0.0722 * data[i+2]);
    hist[lum]++; sumLum += lum; sumSq += lum * lum;
  }
  const mean   = sumLum / total;
  const meanN  = mean / 255;
  const stdDev = Math.sqrt(Math.max(0, sumSq / total - mean * mean)) / 255;
  const shadowPx    = hist.slice(0,   65).reduce((a,b)=>a+b,0);
  const highlightPx = hist.slice(192, 256).reduce((a,b)=>a+b,0);
  return { meanN, stdDev, shadowRatio: shadowPx/total, highlightRatio: highlightPx/total };
}

/** Derive recommended shadows/highlights values optimised for string art. */
function _recommendTone(stats) {
  const { meanN, stdDev, shadowRatio, highlightRatio } = stats;
  let s = 0, h = 0;
  if      (shadowRatio < 0.06)              { s = -30; }
  else if (meanN > 0.62)                    { s = -25; }
  else if (meanN < 0.28 && shadowRatio > 0.55) { s = +35; }
  if      (highlightRatio < 0.04)           { h = +22; }
  else if (highlightRatio > 0.38)           { h = -28; }
  if (stdDev < 0.14) { if (!s) s = -20; if (!h) h = +18; }
  return { shadows: Math.round(s), highlights: Math.round(h) };
}

function _loadImg(src) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
}

/**
 * Apply a tone preset to state.croppedDataUrl, update state.uploadedDataUrl,
 * and call triggerModelUpdate().
 *
 * @param {'original'|'auto'|'low-shadows'|'low-lights'} mode
 */
async function applyToneFilter(mode) {
  const base = state.croppedDataUrl;
  if (!base) return;

  state.toneFilterMode = mode;
  _updateFilterBarUI(mode);

  let shadows = 0, highlights = 0;

  if (mode === 'auto') {
    try {
      const img   = await _loadImg(base);
      const stats = _analyzeImageData(img);
      const rec   = _recommendTone(stats);
      shadows    = rec.shadows;
      highlights = rec.highlights;
    } catch { /* fall back to original on error */ }
  } else if (mode === 'low-shadows') {
    // Sombras bajas, luces altas  →  high contrast / dramatic
    shadows = -50; highlights = 50;
  } else if (mode === 'low-lights') {
    // Sombras altas, luces bajas  →  flat / matte
    shadows = 50; highlights = -50;
  }
  // 'original' → shadows = 0, highlights = 0

  if (shadows === 0 && highlights === 0) {
    state.uploadedDataUrl = base;
    triggerModelUpdate();
    return;
  }

  // Run WebGL filter on a hidden off-screen canvas
  const offCanvas = document.createElement('canvas');
  offCanvas.style.cssText = 'position:fixed;top:-9999px;left:-9999px;visibility:hidden;pointer-events:none;';
  document.body.appendChild(offCanvas);

  try {
    const filter = new ImageToneFilter(offCanvas);
    await filter.loadImage(base);
    filter.setShadows(shadows);
    filter.setHighlights(highlights);
    filter.render();
    state.uploadedDataUrl = filter.toDataURL('image/jpeg', 0.92);
    filter.destroy();
  } catch (err) {
    console.warn('[ToneFilter] WebGL filter failed, using original:', err);
    state.uploadedDataUrl = base;
  } finally {
    document.body.removeChild(offCanvas);
  }

  triggerModelUpdate();
}

const _filterLabels = {
  'original':    'Original',
  'auto':        'Auto',
  'low-shadows': 'Low shadows · high lights',
  'low-lights':  'High shadows · low lights',
};

function _updateFilterBarUI(mode) {
  const labelEl = document.getElementById('cfg-filter-label');
  if (labelEl) labelEl.textContent = _filterLabels[mode] || mode;

  document.querySelectorAll('.cfg-filter-btn').forEach(btn => {
    const active = btn.dataset.filterMode === mode;
    if (active) {
      btn.style.background = 'rgba(24,24,27,0.92)';
      btn.style.color = '#fff';
    } else {
      btn.style.background = 'rgba(255,255,255,0.9)';
      btn.style.color = '#52525b';
    }
  });
}

function _showFilterBar(visible) {
  const bar = document.getElementById('cfg-filter-bar');
  if (bar) bar.style.display = visible ? 'block' : 'none';
}

function setupFilterBar() {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.cfg-filter-btn');
    if (!btn || !state.croppedDataUrl) return;
    const mode = btn.dataset.filterMode;
    if (mode) applyToneFilter(mode);
  });
  _updateFilterBarUI(state.toneFilterMode);
}

// ---------------------------------------------------------------------------
// Event binding
// ---------------------------------------------------------------------------

function bindEvents() {
  // Crop modal
  document.getElementById('cfg-crop-confirm')?.addEventListener('click', _confirmCrop);
  document.getElementById('cfg-crop-cancel')?.addEventListener('click', _closeCropModal);

  // Navigation buttons
  document.getElementById('cfg-prev-btn')?.addEventListener('click', () => navigate(-1));
  document.getElementById('cfg-next-btn')?.addEventListener('click', () => navigate(1));

  // Step dots — direct step jump
  document.querySelectorAll('[data-cfg-step-dot]').forEach(dot => {
    dot.addEventListener('click', () => {
      const idx = Number(dot.dataset.cfgStepDot);
      if (idx >= 0 && idx !== state.currentStep) {
        const prev = state.currentStep;
        state.currentStep = idx;
        history.pushState({ step: idx }, '');
        renderCurrentStep(prev, idx);
        scrollToStepTop();
      }
    });
  });

  // Keyboard arrows
  document.addEventListener('keydown', (e) => {
    const tag = e.target?.tagName;
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return;
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
    if (e.key === 'ArrowRight') { e.preventDefault(); navigate(1); }
    if (e.key === 'ArrowLeft')  { e.preventDefault(); navigate(-1); }
  });

  // Upload
  const fileInput = document.getElementById('cfg-file-input');
  document.getElementById('cfg-upload-btn')?.addEventListener('click', () => fileInput?.click());
  setupUpload(fileInput);

  // Tone filter bar
  setupFilterBar();

  // Selection cards (event delegation)
  document.addEventListener('click', (e) => {
    const card = e.target.closest('[data-cfg-size-key]');
    const styleCard = e.target.closest('[data-cfg-style-key]');
    const ftCard = e.target.closest('[data-cfg-frame-type-key]');
    const fcCard = e.target.closest('[data-cfg-frame-color-key]');
    const atcBtn = e.target.closest('#cfg-add-to-cart');

    if (card) handleSizeSelect(card);
    else if (styleCard) handleStyleSelect(styleCard);
    else if (ftCard) handleFrameTypeSelect(ftCard);
    else if (fcCard) handleFrameColorSelect(fcCard);
    else if (atcBtn) addToCart();
  });

  // Extras (express shipping, designer editing)
  document.getElementById('cfg-extra-express')?.addEventListener('change', (e) => {
    state.expressShipping = e.target.checked;
    updatePriceDisplay();
    updateSummary();
  });

  document.getElementById('cfg-extra-designer')?.addEventListener('change', (e) => {
    state.designerEditing = e.target.checked;
    const textarea = document.getElementById('cfg-designer-instructions');
    if (textarea) textarea.hidden = !e.target.checked;
    updatePriceDisplay();
    updateSummary();
  });

  document.getElementById('cfg-designer-instructions')?.addEventListener('input', (e) => {
    state.designerInstructions = e.target.value;
  });

  // Browser back/forward
  window.addEventListener('popstate', (e) => {
    if (e.state?.step != null) {
      state.currentStep = e.state.step;
      renderCurrentStep(null, state.currentStep);
      scrollToStepTop();
    }
  });
}

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

async function init() {
  const root = document.getElementById('cfg-viewer-container');
  if (!root) return;

  const viewer3dUrl = root.dataset.viewer3dUrl;
  const currency    = root.dataset.currency || 'USD';
  state.currency    = currency;

  // Resolve patch asset URLs injected by Liquid
  const patchUrls = [];
  for (let i = 0; i < 7; i++) {
    patchUrls.push(root.dataset[`patch${i}Url`]);
  }

  try {
    // Only fetch the technical viewer config (camera/lighting/shader params).
    // All merchant-editable data (sizes, styles, frame colors, prices) comes
    // from window.cfgConfig which is generated by Liquid from section blocks.
    const viewer3d = await fetch(viewer3dUrl).then(r => r.json());
    const config   = buildConfigFromLiquid(window.cfgConfig || {}, viewer3d);

    config._assetUrls = {
      patches:       patchUrls,
      backgrounds:   config._backgrounds || {},
      frameStandard: root.dataset.frameStandardUrl,
      frameCorner:   root.dataset.frameCornerUrl,
    };
    delete config._backgrounds;
    state.config = config;
    state.steps  = buildSteps();

    // Initialize viewer (non-fatal — UI remains functional if 3D fails)
    try {
      viewer = new Viewer3D(root, config);
      await viewer.initialize();
      root._cfgViewer = viewer;
    } catch (viewerErr) {
      console.error('[Configurator] 3D viewer init failed (non-fatal):', viewerErr);
      viewer = null;
    }

    // Set default selections from first options in config
    const sizeKeys = Object.keys(config.size_type || {});
    if (sizeKeys.length) setSelection('size', sizeKeys[0]);

    const styleKeys = Object.keys(config.style || {});
    if (styleKeys.length) setSelection('style', styleKeys[0]);

    const ftKeys = Object.keys(config.frame_type || {});
    const defaultFt = ftKeys.find(k => config.frame_type[k].has_colors) || ftKeys[0];
    if (defaultFt) {
      setSelection('frame_type', defaultFt);
      const fcKeys = Object.keys(config.frame_color || {});
      if (fcKeys.length) setSelection('frame_color', fcKeys[0]);
    }

    // Sync variant IDs from pre-selected DOM cards (set via Liquid data-selected)
    const preStyleCard = document.querySelector('[data-cfg-style-key][data-selected]');
    if (preStyleCard) setSelection('style_variant_id', preStyleCard.dataset.variantId || null);

    const preFtCard = document.querySelector('[data-cfg-frame-type-key][data-selected]');
    if (preFtCard) setSelection('frame_type_variant_id', preFtCard.dataset.variantId || null);

    const preFcCard = document.querySelector('[data-cfg-frame-color-key][data-selected]');
    if (preFcCard) setSelection('frame_color_variant_id', preFcCard.dataset.variantId || null);

    // Bind all events
    bindEvents();

    // Initial render
    renderCurrentStep(0, 0);

    // Show default model (grey gradient placeholder) so 3D scene is visible before upload
    await showDefaultModel();

    // Fit height to visible viewport (accounts for Shopify page header, desktop only)
    fitToViewport();

    // After CSS has settled, tell Three.js the real container size so camera
    // re-frames correctly (CSS !important overrides adjustResponsiveHeight inline style).
    if (viewer) {
      requestAnimationFrame(() => viewer.handleResize());
    }

    console.log('[Configurator] Initialized successfully');
  } catch (err) {
    console.error('[Configurator] Init failed:', err);
  }
}

// ---------------------------------------------------------------------------
// Default demo model (shows plate/frame before user uploads photo)
// ---------------------------------------------------------------------------

async function showDefaultModel() {
  if (!viewer || !state.selected.size) return;

  try {
    const sizeConfig  = state.config.size_type[state.selected.size];
    const isCircular  = state.config.configurator_type === 'circular';

    const canvas = document.createElement('canvas');

    if (isCircular) {
      const diameter   = sizeConfig.circle_diameter    || 300;
      const multiplier = sizeConfig.art_size_multiplier || 3;
      const sizePx     = Math.round(diameter * multiplier);
      canvas.width  = Math.max(sizePx, 60);
      canvas.height = Math.max(sizePx, 60);
    } else {
      const pxPerCm = state.config.string_art_shader?.src_px_per_cm || 30;
      const w = Math.round(((sizeConfig.nail_area_x || 519) / 10) * pxPerCm);
      const h = Math.round(((sizeConfig.nail_area_y || 366) / 10) * pxPerCm);
      canvas.width  = Math.max(w, 60);
      canvas.height = Math.max(h, 60);
    }

    const pCtx = canvas.getContext('2d');
    const pGrad = pCtx.createLinearGradient(0, 0, canvas.width, canvas.height);
    pGrad.addColorStop(0, '#e8e8e8');
    pGrad.addColorStop(1, '#d0d0d0');
    pCtx.fillStyle = pGrad;
    pCtx.fillRect(0, 0, canvas.width, canvas.height);

    state._isDefaultPlaceholder = true;
    state.uploadedDataUrl = canvas.toDataURL('image/jpeg', 0.85);

    await viewer.updateModel(state);
  } catch (e) {
    console.warn('[Configurator] Default model failed (non-critical):', e);
  }
}

// ---------------------------------------------------------------------------
// Viewport fit — compensates for Shopify header height so cfg-root fills
// exactly the visible viewport (window.innerHeight minus the section's top).
// ---------------------------------------------------------------------------

function fitToViewport() {
  const root = document.querySelector('.cfg-root');
  if (!root) return;
  if (window.innerWidth < 1024) {
    root.style.height = '';   // mobile: CSS controls, natural scroll
    return;
  }
  const top = root.getBoundingClientRect().top;
  root.style.height = Math.max(window.innerHeight - top, 400) + 'px';
}

let _cfgResizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(_cfgResizeTimer);
  _cfgResizeTimer = setTimeout(fitToViewport, 80);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
